<template>

  <el-form ref='licenseeForm' v-model="telco" label-position="top">
    <el-row :gutter="24">

      <el-col :span="12">
        <el-form-item label="Sharer">
          <strong>{{sharerName}}</strong>
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item label="Telco Site ID" required>
          <el-input placeholder="Site ID" v-model="telco.telcoSiteId" :disabled="stepSaved || canEdit" />
        </el-form-item>
      </el-col>
    </el-row>

    <section  v-if="!canEdit">
      <el-row :gutter="12">
        <el-col :md="24" :lg="12">
          <el-form-item label="Sharer No.">
            <el-input-number v-model="telco.sharerNo" :min="1" :max="99" :step="1"
            controls-position="right" size="small" placeholder="Auto" :disabled="stepSaved || canEdit" />
            <div>Leave blank to auto-assign the next free number.</div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="12">
        <el-col :md="24" :lg="12">
          <select-service-type v-model="telco.serviceType"  :disabled="stepSaved || canEdit" />
        </el-col>

        <el-col  :md="24" :lg="12">
          <select-spectrum v-model="telco.spectrumBand"  :disabled="stepSaved || canEdit" />
        </el-col>
      </el-row>

      <el-row :gutter="12">
        <el-col :md="24">
          <sharer-rate-override
            v-model="telco.rate_id"
            :sharer-id="sharerId"
            :disabled="stepSaved || canEdit" />
        </el-col>
      </el-row>

      <!-- XOL-467: any-to-all auto-fill notice. Source-agnostic copy — any of
           Handover/Agreement Start/Billing Start can be the field the user
           actually typed. -->
      <el-row v-if="autoFilled" :gutter="12">
        <el-col :md="24">
          <el-alert type="info" show-icon :closable="false"
            description="Any dates that were still blank have been filled in from the date you just set. Adjust them if they differ." />
        </el-col>
      </el-row>

      <el-row :gutter="12">
        <el-col :md="24" :lg="12">
          <el-form-item label='Handover Date' required class="licensee-date--editing" :error="fieldErrors.handover">
            <el-date-picker ref="dateField_handover" value-format="yyyy-MM-dd" :format="format"
            v-model="telco.handover"  :disabled="stepSaved || canEdit" @change="onDateChange('handover', $event)" />
          </el-form-item>
        </el-col>

        <el-col :md="24" :lg="12">
          <el-form-item label='Agreement Start' required class="licensee-date--editing" :error="fieldErrors.started">
            <el-date-picker ref="dateField_started" value-format="yyyy-MM-dd" :format="format"
            v-model="telco.started"  :disabled="stepSaved || canEdit" @change="onDateChange('started', $event)" />
          </el-form-item>
        </el-col>

        <!-- XOL-467: Billing Start was never collected here, so a new sharer had no
             way to say when billing begins. Left blank the API derives it — from
             the commencement date, or from the handover when that is blank too —
             but deriving it is the fallback, not the intent. -->
        <el-col :md="24" :lg="12">
          <el-form-item label='Billing Start' class="licensee-date--editing" :error="fieldErrors.startBilling">
            <el-date-picker ref="dateField_startBilling" value-format="yyyy-MM-dd" :format="format"
            v-model="telco.startBilling"  :disabled="stepSaved || canEdit" @change="onDateChange('startBilling', $event)" />
          </el-form-item>
        </el-col>

        <el-col :md="24" :lg="12">
          <el-form-item label='On Air Date' class="licensee-date--editing">
            <el-date-picker value-format="yyyy-MM-dd" :format="format"
            v-model="telco.onAir"  :disabled="stepSaved || canEdit" @change="clearDateErrors" />
          </el-form-item>
        </el-col>

        <el-col :md="24" :lg="12">
          <el-form-item label='Expiry Date' class="licensee-date--editing" :error="fieldErrors.expiry">
            <el-date-picker ref="dateField_expiry" value-format="yyyy-MM-dd" :format="format"
            label='Contract End' v-model='expiryDate' disabled />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="8">
        <el-col :span="12">
          <el-form-item prop="capex" label="CAPEX">
            <el-input type="number" placeholder="CAPEX"
            v-model="telco.capex"  :disabled="stepSaved || canEdit"/>
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item label="OPEX">
            <el-input type="number" placeholder="OPEX" v-model="telco.opex"
            :disabled="stepSaved || canEdit" />
          </el-form-item>
        </el-col>
      </el-row>


      <el-row :gutter="8">
        <el-col :span="12">
          <el-form-item>
            Cabins &nbsp;<el-input-number v-model="telco.cabin" :min="0" :max="5" size="mini" :disabled="stepSaved || canEdit" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <check-box v-model="telco.ownMeter"  :disabled="stepSaved || canEdit">{{'Own  ' + utilLabel + ' Meter'}}</check-box>
        </el-col>
      </el-row>

      <el-form-item label='Tenor (years)'>
        <el-slider
        v-model="tenor" :min="1" :max="20" show-stops show-input  :disabled="stepSaved || canEdit">
      </el-slider>
    </el-form-item>

    <el-row>
      <el-col align="middle">
        <el-button :plain="true" type="success" icon="fa fa-check" @click="onSave" :disabled="stepSaved || canEdit">Save</el-button>
        <!-- XOL-596 (completed steps): the super's re-enable is not rendered while the step is complete. -->
        <el-button v-if="isSuper() && !frozen" :plain="true" type="error" icon="fa fa-edit" @click="stepSaved=false"></el-button>
      </el-col>
    </el-row>
</section>
</el-form>

</template>


<script>

import {settings} from '@/components/Data'

import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'
import DateChainMixin from '@/mixins/DateChainMixin'
import {dateChainErrors, autoFillDates} from '@/lib/licenseeDates'

import SelectServiceType from '@/components/atoms/selects/SelectServiceType'
import SelectSpectrum from '@/components/atoms/selects/SelectSpectrum'
import SharerRateOverride from '@/components/finance/rates/SharerRateOverride'

// XOL-467 (any-to-all widening): the three fields this form can both source
// and target auto-fill from/to. See licenseeDates.js's autoFillDates() doc.
var DATE_CHAIN_FIELDS = ['handover', 'started', 'startBilling']

export default {
  mixins: [DateMixins, PostMixins, DateChainMixin],

  components: {
    SelectServiceType,
    SelectSpectrum,
    SharerRateOverride
  },

  props: {
    siteId: {
      type: [String, Number],
      required: true
    },

    sharerName: {
      type: String,
      required: true
    },

    sharerId: {
      type: Number,
      required: true
    },

    editable: {
      type: Boolean,
      default: false
    },

    saved: {
      type: Boolean,
      default: false
    },

    // XOL-596 (completed steps): Create Sharer is used from RTS step 3 (party 1): that
    // row's `action_steps.id` is sent with the create so the API refuses it while the step is
    // complete (nothing is created), and `frozen` hides the super's re-enable button.
    actionStepId: {
      type: Number,
      default: null
    },

    frozen: {
      type: Boolean,
      default: false
    },

    sharerSiteId: {
      type: String,
      default: null
    },

    serviceType: {
      type: String,
      default: null
    },

    spectrumBand: {
      type: String,
      default: null
    },

    equipment: {
      type: Array,
      default: []
    }
  },

  methods: {
    // Pushes posts to the server when called.
    resetData () {
      this.telco = this.getTableStruct('licensee')
      this.telco.site_id = this.siteId
      this.autoFilled = false
    },

    // XOL-416 (was XOL-376 Inc 4): prefill the Sharer No. from the server-suggested
    // next free slot. {suggested:N} comes back as raw JSON, no data wrapper.
    applyBillingSuggestion (data) {
      this.telco.sharerNo = (data && data.suggested) ? data.suggested : null
    },

    // fetchData already toasts on failure — no extra message here. Leaving it
    // blank is safe: the server assigns MAX(Active)+1 when the key is omitted,
    // which is the same number the suggestion endpoint would have returned.
    onSuggestionFail () {
      this.telco.sharerNo = null
    },

    // XOL-467: field -> message for `dateChainErrors()`. Every chain field
    // has an input on this form, so nothing needs rerouting.
    dateChainCopy (item) {
      switch (item.code) {
        case 'handover_required':
          return {field: 'handover', message: 'Required when Agreement Start is set.'}
        case 'started_required':
          return {field: 'started', message: 'Required when Billing Start is set.'}
        case 'billing_start_required':
          return {field: 'startBilling', message: 'Required when Agreement Start is set.'}
        case 'billing_before_started':
          return {
            field: 'startBilling',
            message: 'Cannot be before Agreement Start (' + this.formatDate(this.telco.started) + ').'
          }
        case 'expiry_before_billing_start':
          return {field: 'expiry', message: 'Must be after Billing Start.'}
        default:
          return null
      }
    },

    // XOL-467: not wrapped in a Popup, so it needs its own gate — there is
    // no `beforeSave` prop to hook here.
    validateDates () {
      var self = this
      // XOL-483: `true` — this form only ever POSTs (see onSave/saveForm),
      // so it must judge the row the API's create-time cascade produces, not
      // the raw form values. See licenseeDates.js's own docblock.
      var errors = dateChainErrors(this.telco, true)
      var map = {}

      errors.forEach(function (item) {
        var copy = self.dateChainCopy(item)
        if (copy && !map[copy.field]) {
          map[copy.field] = copy.message
        }
      })

      this.fieldErrors = map

      var fields = Object.keys(map)
      if (fields.length > 0) {
        this.notifyBlockedSave(fields.length)
        this.focusDateField(fields[0])
        return false
      }

      return true
    },

    // Errors clear on ANY date change — the whole map, since the rules are
    // cross-field. Every chain field's change also checks for any-to-all
    // auto-fill (XOL-467).
    onDateChange (field, value) {
      this.clearDateErrors()
      this.maybeAutoFillDates(field, value)
    },

    // XOL-467 (any-to-all widening): any of Handover/Agreement Start/Billing
    // Start can be the source. Fills each of the other two that is still empty; a value you typed is never a target
    // before this edit — the same gate doubles as the "do not fight the
    // user" re-arm condition (see autoFillDates()'s own doc in
    // licenseeDates.js). Never fires once the step is saved / read-only —
    // every date-input here is `:disabled="stepSaved || canEdit"` (this
    // form's inverted naming: `canEdit` true means the step is LOCKED).
    maybeAutoFillDates (field, value) {
      if (this.stepSaved || this.canEdit) return

      var updates = autoFillDates(this.telco, field, value, DATE_CHAIN_FIELDS)
      var keys = Object.keys(updates)
      if (keys.length === 0) return

      var self = this
      keys.forEach(function (key) { self.telco[key] = updates[key] })
      this.autoFilled = true
    },

    onSave () {
      this.telco.site_id = this.siteId
      this.telco.organisation_id = this.sharerId

      // XOL-416: omit `sharerNo` unless it is a valid 1-99 int, so the server
      // assigns MAX(Active)+1 itself. The meta struct seeds numeric fields to 0
      // and a failed/skipped prefill leaves null — sending either would be wrong
      // here: 0 is the deliberate UNASSIGNED sentinel and must never be set by
      // accident at create time, and null is a no-op the server would ignore.
      var n = parseInt(this.telco.sharerNo, 10)
      if (!(n >= 1 && n <= 99)) {
        delete this.telco.sharerNo
      } else {
        this.telco.sharerNo = n
      }

      if (this.telco.started === 'invalid date') {
        this.telco.started = null
      }

      if (this.telco.expiry === 'invalid date') {
        this.telco.expiry = null
      }

      this.telco.outdoor = !this.telco.cabin

      this.telco.started = this.formatDateSql(this.telco.started)
      this.telco.expiry = this.formatDateSql(this.telco.expiry)

      this.telco.handover = this.formatDateSql(this.telco.handover)
      this.telco.startBilling = this.formatDateSql(this.telco.startBilling)
      this.telco.onAir = this.formatDateSql(this.telco.onAir)

      // XOL-467: blocking, field-level — no PUT/POST when the resulting row
      // would violate the chain.
      if (!this.validateDates()) return

      if (this.telco && this.equipment && this.equipment.length > 0) {
        this.telco.equipment = this.equipment
      }

      // The step context rides in the body (XOL-596): a refusal (403 step_completed) creates nothing.
      if (this.actionStepId) {
        this.telco.actionStepId = this.actionStepId
      }

      this.saveForm(this, '/licensees', this.telco.id, this.telco, 'Sharer', this.success, this.failed)
    },

    success (response) {
      if (this.telco.cabin > 0) {
        let cabinAsset = {}
        cabinAsset.owner_id = this.telco.organisation_id
        cabinAsset.site_id = this.telco.site_id
        cabinAsset.category = 'Buildings'
        cabinAsset.type = 'Cabin'
        cabinAsset.qty = this.telco.cabin
        cabinAsset.isAuditable = true
        cabinAsset.usesPower = true
        cabinAsset.isExternal = true
        this.saveForm(this, '/assets', null, cabinAsset,  'Cabins', null, null, false)
      }


      if (this.telco.ownMeter) {
        let meterAsset = {}
        meterAsset.owner_id = this.telco.organisation_id
        meterAsset.site_id = this.telco.site_id
        meterAsset.category = 'Power'
        meterAsset.type = 'Own Meter'
        meterAsset.qty = 1
        meterAsset.isAuditable = false
        meterAsset.providesPower = true
        meterAsset.usesPower = false
        meterAsset.isExternal = true
        this.saveForm(this, '/assets', null, meterAsset, 'Own Meter', null, null, false)
      }

      this.stepSaved = true
    },

    failed (response) {
      console.error('Sharer Form save failed:', response)
      this.stepSaved = false

      // A completed step (XOL-596): the request drawer says why. `response` is the axios error
      // for the refusal (PostMixins calls this for step_completed whatever the opt-in).
      this.stepCompletedRefusal(response, 'sharer', { actionStepId: this.actionStepId })
    }
  },

  computed: {
    expiryDate: function () {
      this.telco.expiry = this.contractExpiryDate(this.telco.started, this.tenor, 'years', 'YYYY-MM-DD')
      return this.telco.expiry
    }
  },

  data () {
    return {
      format: settings.selectDateFormat,
      tenor: 10,
      canEdit: this.editable,
      stepSaved: this.saved,
      utilLabel: this.getUtilLabel(),
      telco: this.getTableStruct('licensees'),
      // XOL-467: any-to-all auto-fill notice flag — see maybeAutoFillDates().
      autoFilled: false
    }

  }, // data


  created () {
    this.telco.telcoSiteId = this.sharerSiteId
    this.telco.spectrumBand = this.spectrumBand
    this.telco.serviceType = this.serviceType

    // XOL-416: only prefill the Sharer No. suggestion in create mode — an
    // already-saved/editable step already has its number. Clear the meta-seeded 0
    // first so the field shows the "Auto" placeholder while the suggestion is in
    // flight, instead of a flash of "0" (which now reads as "unassigned").
    if (!this.saved && !this.editable && this.siteId) {
      this.telco.sharerNo = null
      this.fetchData('licensees/next-sharer-no?site_id=' + this.siteId, this.applyBillingSuggestion, 'next sharer number', this.onSuggestionFail)
    }
  }
}

</script>

<style scoped>
/* XOL-467: reserve room for the absolutely-positioned .el-form-item__error so
   a blocked-save message can never overlap the CAPEX/OPEX row below. Scoped
   to this custom class only (not a bare ::v-deep .el-form-item rule — that
   leaks into every nested form, XOL-451). */
.licensee-date--editing {
  margin-bottom: 56px !important;
}

.licensee-date--editing ::v-deep .el-form-item__error {
  line-height: 1.4;
}
</style>
